import { supabase } from '../lib/supabase';
import { Draft } from '../core/models';
import { Store, StoreError, Unsubscribe } from './Store';
import { extractPlainText, createEmptyLexicalState, createLexicalState } from '../core/lexical';

export class SupabaseStore implements Store {
  private listeners: Set<() => void> = new Set();
  private userId: string | null = null;

  constructor(initialUserId?: string) {
    // Seed with known user id to avoid an early auth race
    this.userId = initialUserId || null;

    // Get initial user (fallback if not provided)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!this.userId) {
        this.userId = session?.user?.id || null;
      }
    });

    // Listen for auth changes
    supabase.auth.onAuthStateChange((_event, session) => {
      this.userId = session?.user?.id || null;
      this.notifyListeners();
    });
  }

  private async ensureUserId(): Promise<void> {
    if (this.userId) return;
    const { data: { session } } = await supabase.auth.getSession();
    this.userId = session?.user?.id || null;
  }

  private normalizeToLexical(body: unknown): string {
    if (typeof body !== 'string' || body.trim() === '') {
      return createEmptyLexicalState();
    }
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === 'object' && (parsed as any).root?.type === 'root') {
        return JSON.stringify(parsed);
      }
      // If it is JSON but not a Lexical doc, treat as plain text
      return createLexicalState(body);
    } catch {
      // Not JSON; treat as plain text
      return createLexicalState(body);
    }
  }

  async get(id: string): Promise<Draft | null> {
    try {
      await this.ensureUserId();
      if (!this.userId) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('id', id)
        .eq('user_id', this.userId)
        .single();

      if (error) throw error;
      if (!data) return null;

      const raw = this.normalizeToLexical(data.body);
      return {
        id: data.id,
        raw,
        plainText: extractPlainText(raw),
        updated: new Date(data.updated_at).getTime(),
        platforms: {
          twitter: true,
          bluesky: true,
          farcaster: true
        }
      };
    } catch (error) {
      throw new StoreError(
        `Failed to get draft ${id}`,
        'get',
        error
      );
    }
  }

  async list(): Promise<Draft[]> {
    try {
      await this.ensureUserId();
      if (!this.userId) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', this.userId)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      return data.map(note => {
        const raw = this.normalizeToLexical(note.body);
        return {
          id: note.id,
          raw,
          plainText: extractPlainText(raw),
          updated: new Date(note.updated_at).getTime(),
          platforms: {
            twitter: true,
            bluesky: true,
            farcaster: true
          }
        };
      });
    } catch (error) {
      throw new StoreError(
        'Failed to list drafts',
        'list',
        error
      );
    }
  }

  async put(draft: Draft): Promise<void> {
    try {
      await this.ensureUserId();
      if (!this.userId) {
        throw new Error('Not authenticated');
      }

      const { error } = await supabase
        .from('notes')
        .upsert({
          id: draft.id,
          user_id: this.userId,
          body: draft.raw,
          updated_at: new Date(draft.updated).toISOString()
        });

      if (error) throw error;
      this.notifyListeners();
    } catch (error) {
      throw new StoreError(
        `Failed to save draft ${draft.id}`,
        'put',
        error
      );
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await this.ensureUserId();
      if (!this.userId) {
        throw new Error('Not authenticated');
      }

      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', id)
        .eq('user_id', this.userId);

      if (error) throw error;
      this.notifyListeners();
    } catch (error) {
      throw new StoreError(
        `Failed to delete draft ${id}`,
        'remove',
        error
      );
    }
  }

  onChange(callback: () => void): Unsubscribe {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }
} 